import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "dashboard" }
const _hoisted_2 = { class: "head" }
const _hoisted_3 = { class: "head-title" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { class: "head-subheader" }
const _hoisted_6 = { class: "head-links" }
const _hoisted_7 = {
  href: "https://epinio.io/",
  target: "_blank",
  rel: "noopener noreferrer nofollow"
}
const _hoisted_8 = {
  href: "https://github.com/epinio/epinio/issues",
  target: "_blank",
  rel: "noopener noreferrer nofollow"
}
const _hoisted_9 = { class: "get-started" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { key: 2 }
const _hoisted_13 = {
  key: 1,
  class: "link disabled"
}

import { Location } from 'vue-router';
import { useStore } from 'vuex';
import { ref, onMounted, computed, watch } from 'vue';
import {
  EpinioApplicationResource,
  EpinioCatalogService,
  EPINIO_MGMT_STORE,
  EPINIO_TYPES,
} from '../../../types';

import { sortBy } from 'lodash';
import isEqual from 'lodash/isEqual';

import { METRIC } from '@shell/config/types';
import { allHash } from '@shell/utils/promise';
import Namespace from '@shell/models/namespace';
import Banner from '@components/Banner/Banner.vue';
import EpinioServiceModel from '../../../models/services';
import { parseSi, createMemoryValues } from '@shell/utils/units';
import { createEpinioRoute } from '../../../utils/custom-routing';
import DashboardCard from '../../../components/dashboard/Cards.vue';
import ConsumptionGauge from '@shell/components/ConsumptionGauge.vue';

type ComponentService = {
  name: string,
  link: Location,
  isEnabled: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'dashboard',
  setup(__props) {

const store = useStore();
const t = store.getters['i18n/t'];
const colorStops = {
  0: '--info',
  30: '--info',
  70: '--info',
};

//Variables that can recieve updates
const version = ref<string>('');
const showMetricsInfo = ref<boolean>(false);
const availableCpu = ref<number>(100);
const availableMemory = ref<number>(100);
const sectionContent = ref<Array>([
  {
    isEnable: true,
    isLoaded: false,
    icon: 'icon-namespace',
    cta: createEpinioRoute(
      'c-cluster-resource',
      { resource: EPINIO_TYPES.NAMESPACE },
      { query: { mode: 'openModal' }, }
    ),
    link: createEpinioRoute(
      'c-cluster-resource',
      { resource: EPINIO_TYPES.NAMESPACE },
    ),
    linkText:    t('epinio.intro.cards.namespaces.linkText'),
    description: t('typeDescription.namespaces', {}, true),
    slotTitle:   t('epinio.intro.cards.namespaces.slotTitle')
  },
  {
    isEnable: true,
    isLoaded: false,
    icon: 'icon-application',
    cta: createEpinioRoute(
      'c-cluster-applications-createapp',
      { resource: EPINIO_TYPES.APP },
    ),
    link: createEpinioRoute(
      'c-cluster-applications',
      { resource: EPINIO_TYPES.APP },
    ),
    linkText: t('epinio.intro.cards.applications.linkText'),
    description: t('typeDescription.applications'),
    slotTitle: '',
  },
  {
    isEnable: true,
    isLoaded: false,
    icon: 'icon-service',
    cta: createEpinioRoute(
      'c-cluster-resource-create',
      { resource: EPINIO_TYPES.SERVICE_INSTANCE },
    ),
    link: createEpinioRoute(
      'c-cluster-resource',
      { resource: EPINIO_TYPES.SERVICE_INSTANCE },
    ),
    linkText: t('epinio.intro.cards.services.linkText'),
    // INFO: typeDescription to long for the dashboard card.
    description: t('epinio.intro.cards.services.description'),
    slotTitle: t('epinio.intro.cards.services.slotTitle')
  },
]);

/*
* Computed Variables
*/

const aboutLink = computed(() => {
  if (!store.getters['isSingleProduct']) {
    return createEpinioRoute(
      'c-cluster-about',
      { cluster: store.getters['clusterId'] },
    );
  }

  return null;
});

const services = computed(() => {
  const fetchServicesInstances: EpinioServiceModel[] =
    store.getters['epinio/all'](EPINIO_TYPES.SERVICE_INSTANCE);
  const fetchServices: EpinioCatalogService[] =
    store.getters['epinio/all'](EPINIO_TYPES.CATALOG_SERVICE);

  // Try to find the desired services
  const findDesiredServices = fetchServices?.filter(
    (service) => service.id === 'mysql-dev' || service.id === 'redis-dev'
  );

  //  if not found, return the first two services from the catalog
  const services: EpinioCatalogService[] =
    findDesiredServices.length ? findDesiredServices : fetchServices.slice(0, 2);

  const s = services.reduce(
    (acc: ComponentService[], service: EpinioCatalogService) => {
      acc.push({
        link: createEpinioRoute(
          'c-cluster-resource-create',
          { resource: EPINIO_TYPES.SERVICE_INSTANCE, name: service.id },
          { query: { service: service.id } }
        ),
        name: service.name,
        isEnabled: true
      });

      return acc;
    },
    [] as ComponentService[]
  );

  return {
    servicesInstances: fetchServicesInstances.length,
    servicesCatalog:   s,
  };
});

const apps = computed(() => {
  const allApps = store.getters['epinio/all'](EPINIO_TYPES.APP) as EpinioApplicationResource[];

  return allApps.reduce((acc, item) => {
    return {
      runningApps: acc.runningApps + (item.status === 'running' ? 1 : 0),
      stoppedApps: acc.stoppedApps + (item.status === 'error' ? 1 : 0),
      totalApps:   acc.totalApps + 1,
    };
  }, {
    runningApps: 0, stoppedApps: 0, totalApps: 0
  });
});

const namespaces = computed(() => {
  const allNamespaces: Namespace[] = store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE);

  return {
    totalNamespaces: allNamespaces.length,
    latestNamespaces: sortBy(allNamespaces, 'metadata.createdAt').reverse().slice(0, 2),
  };
});

/*
* Watchers
*/

watch(services, (oldValue, newValue) => {
  if (isEqual(oldValue, newValue)){
    return;
  }

  generateCards();
}, { immediate: true });

watch(apps, (oldValue, newValue) => {
  if (isEqual(oldValue, newValue)){
    return;
  }

  generateCards();
}, { immediate: true });

watch(namespaces, (oldValue, newValue) => {
  if (isEqual(oldValue, newValue)) {
    return;
  }

  generateCards();
}, { immediate: true });

const metricsDetails = computed(() => {
  return {
    name:   'c-cluster-explorer',
    params: { cluster: store.getters['clusterId'] }
  };
});

onMounted(async () => {
  generateCards();
  await getVersionHash();
});

async function getVersionHash() {
  const hash: { [key:string]: any } = await allHash({
    ns: store.dispatch(
      `epinio/findAll`,
      { type: EPINIO_TYPES.NAMESPACE }
    ),
    svc: store.dispatch(
      `epinio/findAll`,
      { type: EPINIO_TYPES.SERVICE_INSTANCE }
    ),
    catalogSvc: store.dispatch(
      `epinio/findAll`,
      { type: EPINIO_TYPES.CATALOG_SERVICE }
    ),
    version: store.dispatch('epinio/version'),
    showMetrics: calcAvailableResources()
  });

  version.value = hash.version;
}

async function calcAvailableResources() {
  if (store.getters['isSingleProduct']) {
    return;
  }

  const nodeMetricsSchema = store.getters[`epinio/schemaFor`](METRIC.NODE);

  if (nodeMetricsSchema) {
    const id = store.getters['clusterId'];

    const nodeMetrics = await store.dispatch(
      `cluster/request`,
      { url: `/k8s/clusters/${ id }/v1/metrics.k8s.io.nodemetrics` },
      { root: true },
    );

    const currentCluster = store.getters[`${ EPINIO_MGMT_STORE }/byId`](EPINIO_TYPES.CLUSTER, id);

    const cpu = {
      total:  parseSi(currentCluster.mgmtCluster?.status?.capacity?.cpu, null),
      useful: parseSi(nodeMetrics.data[0].usage.cpu, null)
    };

    const memory = createMemoryValues(
      currentCluster.mgmtCluster?.status?.capacity?.memory,
      nodeMetrics.data[0].usage.memory,
    );

    availableCpu.value = Math.floor(100 - cpu.useful / cpu.total * 100);
    availableMemory.value = Math.floor(100 - memory.useful / memory.total * 100);

    showMetricsInfo.value = true;
  }
}

function generateCards() {
  // Handles titles
  sectionContent.value[0].title = t(
    'typeLabel.withCount.namespaces',
    { n: namespaces.value?.totalNamespaces },
  );
  sectionContent.value[1].title = t(
    'typeLabel.withCount.applications',
    { n: apps.value?.totalApps },
  );
  sectionContent.value[2].title = t(
    'typeLabel.withCount.services',
    { n: services.value?.servicesInstances },
  );

  // Handles descriptions
  if (namespaces.value.totalNamespaces >= 0) {
    sectionContent.value[0].isLoaded = true;
  }

  if (apps.value.totalApps >= 0) {
    sectionContent.value[1].isLoaded = true;
  }

  if (services.value.servicesCatalog?.length >= 0) {
    sectionContent.value[2].isLoaded = true;
    sectionContent.value[2].isEnable = true;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h1", null, _toDisplayString(_unref(t)('epinio.intro.welcome')), 1),
        _cache[0] || (_cache[0] = _createTextVNode()),
        (version.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(version.value.displayVersion), 1))
          : _createCommentVNode("", true)
      ]),
      _cache[3] || (_cache[3] = _createTextVNode()),
      _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)('epinio.intro.blurb')), 1),
      _cache[4] || (_cache[4] = _createTextVNode()),
      _createElementVNode("p", null, _toDisplayString(_unref(t)('epinio.intro.description')), 1),
      _cache[5] || (_cache[5] = _createTextVNode()),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("a", _hoisted_7, _toDisplayString(_unref(t)('epinio.intro.getStarted')), 1),
        _cache[1] || (_cache[1] = _createTextVNode()),
        _createElementVNode("a", _hoisted_8, _toDisplayString(_unref(t)('epinio.intro.issues')), 1),
        _cache[2] || (_cache[2] = _createTextVNode()),
        (aboutLink.value)
          ? (_openBlock(), _createBlock(_component_RouterLink, {
              key: 0,
              to: aboutLink.value
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('epinio.intro.about')), 1)
              ]),
              _: 1
            }, 8, ["to"]))
          : _createCommentVNode("", true)
      ])
    ]),
    _cache[12] || (_cache[12] = _createTextVNode()),
    (showMetricsInfo.value)
      ? (_openBlock(), _createBlock(Banner, {
          key: 0,
          class: "metrics",
          color: "info"
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(_unref(t)('epinio.intro.metrics.availability', { availableCpu: availableCpu.value, availableMemory: availableMemory.value })), 1),
            _cache[6] || (_cache[6] = _createTextVNode()),
            _createVNode(_component_RouterLink, { to: metricsDetails.value }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('epinio.intro.metrics.link.label')), 1)
              ]),
              _: 1
            }, 8, ["to"])
          ]),
          _: 1,
          __: [6]
        }))
      : _createCommentVNode("", true),
    _cache[13] || (_cache[13] = _createTextVNode()),
    _createElementVNode("div", _hoisted_9, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sectionContent.value, (card, index) => {
        return (_openBlock(), _createElementBlock("div", { key: index }, [
          (card.isEnable)
            ? (_openBlock(), _createBlock(DashboardCard, {
                key: 0,
                "is-loaded": card.isLoaded,
                title: card.title,
                icon: card.icon,
                cta: card.cta,
                link: card.link,
                "link-text": card.linkText,
                description: card.description,
                "slot-title": card.slotTitle
              }, {
                default: _withCtx(() => [
                  (index === 0 && namespaces.value.latestNamespaces.length > 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, [
                        _createElementVNode("ul", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(namespaces.value.latestNamespaces, (ns, i) => {
                            return (_openBlock(), _createElementBlock("li", { key: i }, _toDisplayString(ns.id), 1))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  _cache[10] || (_cache[10] = _createTextVNode()),
                  (index === 1 && apps.value.totalApps > 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                        _createVNode(ConsumptionGauge, {
                          "resource-name": _unref(t)('epinio.intro.cards.applications.running'),
                          capacity: apps.value.totalApps,
                          "used-as-resource-name": true,
                          "color-stops": colorStops,
                          used: apps.value.runningApps,
                          units: "Apps"
                        }, null, 8, ["resource-name", "capacity", "used"])
                      ]))
                    : _createCommentVNode("", true),
                  _cache[11] || (_cache[11] = _createTextVNode()),
                  (index === 2)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, [
                        _createElementVNode("ul", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(services.value.servicesCatalog, (service, i) => {
                            return (_openBlock(), _createElementBlock("li", { key: i }, [
                              (service.isEnabled)
                                ? (_openBlock(), _createBlock(_component_RouterLink, {
                                    key: 0,
                                    to: service.link,
                                    class: "link"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(service.name) + " ", 1),
                                      _cache[7] || (_cache[7] = _createElementVNode("span", null, "+", -1))
                                    ]),
                                    _: 2,
                                    __: [7]
                                  }, 1032, ["to"]))
                                : _createCommentVNode("", true),
                              _cache[9] || (_cache[9] = _createTextVNode()),
                              (!service.isEnabled)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                                    _createTextVNode(_toDisplayString(service.name) + " ", 1),
                                    _cache[8] || (_cache[8] = _createElementVNode("span", null, "+", -1))
                                  ]))
                                : _createCommentVNode("", true)
                            ]))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 2,
                __: [10,11]
              }, 1032, ["is-loaded", "title", "icon", "cta", "link", "link-text", "description", "slot-title"]))
            : _createCommentVNode("", true)
        ]))
      }), 128))
    ])
  ]))
}
}

})